/*
 * Helfer für den Massenemail-Editor: Twig-Ausdrücke ({{ ... }}, {% for %}...{% endfor %},
 * {% if %}...{% endif %}, Einzel-Statements wie {% include %}) werden vor dem Rendern in
 * nicht editierbare Tokens (TinyMCE noneditable, Klasse mceNonEditable) verpackt. Der
 * Twig-Quellcode wandert Base64-kodiert in ein data-twig-Attribut, damit der Server ihn
 * beim Rendern nicht anfasst und er nach dem Bearbeiten wieder extrahiert werden kann.
 *
 * Jedes Token trägt zusätzlich eine eindeutige data-twig-id: TinyMCE kann Elemente beim
 * Editieren splitten/duplizieren — beim Extrahieren wird der Quellcode pro id nur EINMAL
 * eingesetzt, sonst vervielfältigt sich der Inhalt bei jedem Blättern.
 *
 * Ablauf: template --wrapTemplate--> an /api/email_preview --> gerendertes HTML mit
 * Tokens in den Editor; beim Blättern/Senden holt extractTemplate() das (evtl.
 * bearbeitete) Template zurück — Tokens sind dabei nur löschbar, nicht veränderbar.
 */

const TOKEN_CLASS = 'bulk-twig-token';
const TOKEN_SELECTOR = '[data-twig]';

const BLOCK_TAGS = /^(P|DIV|UL|OL|LI|TABLE|TBODY|TR|H[1-6]|BLOCKQUOTE|PRE|HR)$/;

/*
 * Balancierter Twig-Tokenizer. Lazy-Regexe zerlegen VERSCHACHTELTE if/for-Blöcke
 * falsch (der äußere Block endet am ersten endif) — deshalb wird hier sequentiell
 * gescannt und die Blocktiefe gezählt: ein kompletter äußerer Block inkl. aller
 * Verschachtelungen ist EINE Einheit.
 */

//nächstes Twig-Tag ab Position `from`: {{ … }} (expr) oder {% … %} (open/close/branch/statement)
const nextTwigTag = (text, from) => {
    const exprStart = text.indexOf('{{', from);
    const stmtStart = text.indexOf('{%', from);
    if (exprStart === -1 && stmtStart === -1) {
        return null;
    }

    if (stmtStart === -1 || (exprStart !== -1 && exprStart < stmtStart)) {
        const end = text.indexOf('}}', exprStart + 2);
        if (end === -1) {
            return null;
        }
        return { type: 'expr', start: exprStart, end: end + 2 };
    }

    const end = text.indexOf('%}', stmtStart + 2);
    if (end === -1) {
        return null;
    }
    const inner = text.slice(stmtStart + 2, end).trim();
    let type = 'statement';
    if (/^(if|for)\b/.test(inner)) {
        type = 'open';
    } else if (/^end(if|for)\b/.test(inner)) {
        type = 'close';
    } else if (/^(else\b|elseif\b)/.test(inner)) {
        type = 'branch';
    }
    return { type, start: stmtStart, end: end + 2 };
};

//Text → Segmente { type: 'static'|'expr'|'block'|'statement'|'orphan', text }
//'orphan' = Block-Tag ohne balanciertes Gegenstück in DIESEM Text (Teil eines
//elementübergreifenden Blocks — behandelt wrapBlockTokens)
export const tokenizeTwig = text => {
    const segments = [];
    let position = 0;

    const pushStatic = upTo => {
        if (upTo > position) {
            segments.push({ type: 'static', text: text.slice(position, upTo) });
        }
    };

    let guard = 0;
    while (guard++ < 10000) {
        const tag = nextTwigTag(text, position);
        if (!tag) {
            break;
        }

        if (tag.type === 'expr' || tag.type === 'statement') {
            pushStatic(tag.start);
            segments.push({ type: tag.type, text: text.slice(tag.start, tag.end) });
            position = tag.end;
        } else if (tag.type === 'open') {
            //balanciertes Blockende suchen (Tiefe zählen)
            let depth = 1;
            let cursor = tag.end;
            let closeEnd = -1;
            let innerTag = nextTwigTag(text, cursor);
            while (innerTag) {
                if (innerTag.type === 'open') {
                    depth += 1;
                }
                if (innerTag.type === 'close') {
                    depth -= 1;
                    if (depth === 0) {
                        closeEnd = innerTag.end;
                        break;
                    }
                }
                cursor = innerTag.end;
                innerTag = nextTwigTag(text, cursor);
            }

            pushStatic(tag.start);
            if (closeEnd === -1) {
                segments.push({ type: 'orphan', text: text.slice(tag.start, tag.end) });
                position = tag.end;
            } else {
                segments.push({ type: 'block', text: text.slice(tag.start, closeEnd) });
                position = closeEnd;
            }
        } else {
            //close/branch ohne offenen Block in diesem Text
            pushStatic(tag.start);
            segments.push({ type: 'orphan', text: text.slice(tag.start, tag.end) });
            position = tag.end;
        }
    }
    pushStatic(text.length);

    return segments;
};

//Tiefe über einen Text ab Position `from` weiterzählen; depth 0 = balanciertes Ende in diesem
//Text erreicht, end = Position direkt hinter dem schließenden Tag
const continueTagBalance = (text, startDepth, from = 0) => {
    let depth = startDepth;
    let cursor = from;
    let tag = nextTwigTag(text, cursor);
    while (tag) {
        if (tag.type === 'open') {
            depth += 1;
        }
        if (tag.type === 'close') {
            depth -= 1;
            if (depth === 0) {
                return { depth: 0, end: tag.end };
            }
        }
        cursor = tag.end;
        tag = nextTwigTag(text, cursor);
    }
    return { depth, end: -1 };
};

let tokenIdCounter = 0;

const encodeTwig = value => btoa(unescape(encodeURIComponent(value)));
const decodeTwig = value => decodeURIComponent(escape(atob(value)));

const parseHtml = html => new DOMParser().parseFromString(html, 'text/html');

//mode 'text' = Quellcode ist reiner Twig-Text (aus einem Textknoten), 'html' = enthält Markup;
//steuert beim Extrahieren, ob als Textknoten oder als HTML eingesetzt wird (rohes "<" in
//Twig wie "{% if a <b %}" würde als HTML geparst das Template zerschneiden)
const createToken = (doc, tag, twigSource, mode = 'text') => {
    const token = doc.createElement(tag);
    token.className = 'mceNonEditable ' + TOKEN_CLASS;
    //hartes contenteditable=false zusätzlich zur mceNonEditable-Klasse — der Browser
    //verhindert das Editieren dann auch, wenn das noneditable-Plugin nicht greift
    token.setAttribute('contenteditable', 'false');
    token.setAttribute('data-twig', encodeTwig(twigSource));
    token.setAttribute('data-twig-mode', mode);
    tokenIdCounter += 1;
    token.setAttribute('data-twig-id', 'twig-' + tokenIdCounter);
    return token;
};

//Textknoten unterhalb von root in Dokument-Reihenfolge, ohne Inhalte bereits verpackter Tokens
const textNodesBelow = root => {
    const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    const nodes = [];
    while (walker.nextNode()) {
        const node = walker.currentNode;
        if (!node.parentElement || !node.parentElement.closest(TOKEN_SELECTOR)) {
            nodes.push(node);
        }
    }
    return nodes;
};

//[data-twig]-Elemente unterhalb von root durch ihren Twig-Quellcode ersetzen;
//von TinyMCE gesplittete Duplikate (gleiche data-twig-id) werden nur entfernt
const unwrapTokens = root => {
    const seenIds = new Set();

    Array.from(root.querySelectorAll(TOKEN_SELECTOR)).forEach(el => {
        if (!root.contains(el)) {
            //saß in einem bereits ersetzten äußeren Token
            return;
        }

        const id = el.getAttribute('data-twig-id');
        if (id && seenIds.has(id)) {
            el.remove();
            return;
        }
        if (id) {
            seenIds.add(id);
        }

        const source = decodeTwig(el.getAttribute('data-twig'));
        const mode = el.getAttribute('data-twig-mode');
        if (mode === 'text') {
            el.parentNode.insertBefore(root.ownerDocument.createTextNode(source), el);
        } else if (mode === 'table') {
            //Tabellenzeilen nur im Tabellen-Kontext parsen (in einem div fielen <tr>/<td> weg)
            const table = parseHtml('<table>' + source + '</table>').querySelector('table');
            while (table.firstChild) {
                el.parentNode.insertBefore(root.ownerDocument.adoptNode(table.firstChild), el);
            }
        } else {
            const container = root.ownerDocument.createElement('div');
            container.innerHTML = source;
            while (container.firstChild) {
                el.parentNode.insertBefore(container.firstChild, el);
            }
        }
        el.remove();
    });
};

//Twig-Einheiten, die komplett in einem Textknoten liegen → span-Token
//(types steuert, welche Segment-Typen in diesem Durchgang verpackt werden)
const wrapTextNodeTokens = (doc, types) => {
    textNodesBelow(doc.body).forEach(node => {
        const segments = tokenizeTwig(node.nodeValue);
        if (!segments.some(segment => types.includes(segment.type))) {
            return;
        }

        const fragment = doc.createDocumentFragment();
        segments.forEach(segment => {
            if (types.includes(segment.type)) {
                const token = createToken(doc, 'span', segment.text);
                token.textContent = segment.text;
                fragment.appendChild(token);
            } else {
                fragment.appendChild(doc.createTextNode(segment.text));
            }
        });
        node.parentNode.replaceChild(fragment, node);
    });
};

//Blöcke, deren {% for/if %} und {% end... %} in verschiedenen Elementen liegen
//→ betroffene Geschwister-Elemente in ein gemeinsames Token packen
const wrapBlockTokens = doc => {
    let guard = 0;

    while (guard++ < 25) {
        const nodes = textNodesBelow(doc.body);

        //erstes verwaistes öffnendes Block-Tag suchen (balancierte Blöcke innerhalb
        //eines Textknotens sind nach dem Textknoten-Durchgang bereits Tokens)
        let start = null;
        let startIndex = -1;
        for (let i = 0; i < nodes.length && !start; i++) {
            let cursor = 0;
            let tag = nextTwigTag(nodes[i].nodeValue, cursor);
            while (tag) {
                if (tag.type === 'open') {
                    start = { node: nodes[i], tagStart: tag.start, tagEnd: tag.end };
                    startIndex = i;
                    break;
                }
                cursor = tag.end;
                tag = nextTwigTag(nodes[i].nodeValue, cursor);
            }
        }
        if (!start) {
            return;
        }

        //balanciertes Blockende über die folgenden Textknoten hinweg suchen (Tiefe zählen,
        //damit verschachtelte ifs/fors den äußeren Block nicht vorzeitig beenden)
        let end = null;
        let endOffset = -1;
        let balance = continueTagBalance(start.node.nodeValue, 1, start.tagEnd);
        if (balance.depth === 0) {
            end = start.node;
            endOffset = balance.end;
        }
        for (let i = startIndex + 1; end === null && i < nodes.length; i++) {
            balance = continueTagBalance(nodes[i].nodeValue, balance.depth);
            if (balance.depth === 0) {
                end = nodes[i];
                endOffset = balance.end;
            }
        }
        if (!end) {
            //unvollständiger Block — unangetastet lassen (bleibt editierbarer Text)
            return;
        }

        //Textknoten genau an Blockanfang und -ende teilen: Text davor/danach (z.B. ein direkt
        //folgendes {% if %} des nächsten Blocks) gehört nicht in dieses Token
        if (endOffset < end.nodeValue.length) {
            end.splitText(endOffset);
        }
        if (start.tagStart > 0) {
            const blockStart = start.node.splitText(start.tagStart);
            if (end === start.node) {
                end = blockStart;
            }
            start.node = blockStart;
        }

        //gemeinsamen Vorfahren von Start- und End-Knoten bestimmen
        const startAncestors = new Set();
        for (let node = start.node.parentElement; node; node = node.parentElement) {
            startAncestors.add(node);
        }
        let common = end.parentElement;
        while (common && !startAncestors.has(common)) {
            common = common.parentElement;
        }
        if (!common) {
            return;
        }

        let first = start.node;
        while (first.parentNode !== common) {
            first = first.parentNode;
        }
        let last = end;
        while (last.parentNode !== common) {
            last = last.parentNode;
        }

        //span für rein inline verpackte Bereiche, sonst div (gültiges HTML)
        let hasBlockChild = false;
        for (let node = first; node; node = node.nextSibling) {
            if (node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.test(node.tagName)) {
                hasBlockChild = true;
            }
            if (node === last) {
                break;
            }
        }

        const wrapper = createToken(doc, hasBlockChild ? 'div' : 'span', '', 'html');
        common.insertBefore(wrapper, first);

        let node = first;
        while (node) {
            const next = node.nextSibling;
            wrapper.appendChild(node);
            if (node === last) {
                break;
            }
            node = next;
        }

        //Quellcode ohne evtl. schon verpackte innere Tokens speichern
        const clone = wrapper.cloneNode(true);
        unwrapTokens(clone);
        wrapper.setAttribute('data-twig', encodeTwig(clone.innerHTML));
    }
};

/*
 * Twig direkt zwischen Tabellenzeilen/-zellen ({% for %}<tr>…</tr>{% endfor %}, {% if %}<tr>…{% endif %})
 * schiebt der HTML-Parser vor die Tabelle (foster parenting) — Schleife/Bedingung wären von ihren
 * Zeilen getrennt. Deshalb werden solche Tags vor dem Parsen in Kommentare gepackt (die bleiben an
 * Ort und Stelle) und danach die betroffenen Zeilen als eigenes <tbody>-Token verpackt: tbody ist
 * das einzige Element, das mehrere Zeilen umschließen darf, ohne dass ein Parser es verschiebt.
 */
const TABLE_TWIG_COMMENT = 'bulk-twig:';
const TABLE_CONTEXT = /^(table|thead|tbody|tfoot|tr)$/;
const TABLE_SECTION = /^(TABLE|THEAD|TBODY|TFOOT)$/;

const protectTableTwig = html => {
    const stack = [];
    return html.replace(/<(\/?)(table|thead|tbody|tfoot|tr|td|th)\b[^>]*>|{%[\s\S]*?%}|{{[\s\S]*?}}/gi, (match, closing, tag) => {
        if (tag) {
            const name = tag.toLowerCase();
            if (closing) {
                const index = stack.lastIndexOf(name);
                if (index !== -1) {
                    stack.length = index;
                }
            } else {
                stack.push(name);
            }
            return match;
        }
        return TABLE_CONTEXT.test(stack[stack.length - 1] || '') ? '<!--' + TABLE_TWIG_COMMENT + encodeTwig(match) + '-->' : match;
    });
};

//Kommentare wieder zu Twig-Code — HTML-escaped, wie die Serialisierung Twig in Textknoten
//ausgibt; decodeEntitiesInTwig macht daraus danach den rohen Code
const restoreTableTwig = html => html.replace(/<!--bulk-twig:([A-Za-z0-9+/=]*)-->/g, (match, encoded) => decodeTwig(encoded)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;'));

const tableTwigOf = node => (node.nodeType === Node.COMMENT_NODE && node.nodeValue.startsWith(TABLE_TWIG_COMMENT)
    ? decodeTwig(node.nodeValue.slice(TABLE_TWIG_COMMENT.length))
    : null);

//Tiefe der Twig-Kommentare einer Einheit (Kommentar, Zeile oder ganzer Abschnitt) weiterzählen;
//Kommentare in Zellen gehören zu einer verschachtelten Tabelle und zählen nicht mit
const tableUnitBalance = (unit, startDepth) => {
    let nodes = [unit];
    if (unit.nodeType === Node.ELEMENT_NODE) {
        nodes = [];
        const walker = unit.ownerDocument.createTreeWalker(unit, NodeFilter.SHOW_COMMENT, null, false);
        while (walker.nextNode()) {
            const cell = walker.currentNode.parentElement.closest('td, th');
            if (!cell || !unit.contains(cell)) {
                nodes.push(walker.currentNode);
            }
        }
    }
    return nodes.reduce((depth, node) => {
        const twig = tableTwigOf(node);
        return twig === null ? depth : continueTagBalance(twig, depth).depth;
    }, startDepth);
};

const wrapTableTokens = doc => {
    const skipped = new Set();
    let guard = 0;

    while (guard++ < 200) {
        const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_COMMENT, null, false);
        let comment = null;
        while (walker.nextNode()) {
            const node = walker.currentNode;
            if (tableTwigOf(node) !== null && !skipped.has(node) && !(node.parentElement && node.parentElement.closest(TOKEN_SELECTOR))) {
                comment = node;
                break;
            }
        }
        if (!comment) {
            return;
        }

        //Einheit = ganze Zeile (Twig zwischen Zellen) oder der Kommentar selbst (Twig zwischen Zeilen);
        //weitere Geschwister-Einheiten dazunehmen, bis if/for wieder geschlossen ist
        const first = comment.parentNode.tagName === 'TR' ? comment.parentNode : comment;
        const section = first.parentNode;
        if (!section || !TABLE_SECTION.test(section.tagName)) {
            skipped.add(comment);
            continue;
        }
        let last = first;
        let depth = tableUnitBalance(first, 0);
        while (depth > 0 && last.nextSibling) {
            last = last.nextSibling;
            depth = tableUnitBalance(last, depth);
        }
        if (depth !== 0) {
            //Block endet nicht in diesem Tabellenabschnitt — Kommentar bleibt (wird beim Serialisieren wieder Twig)
            skipped.add(comment);
            continue;
        }

        const token = createToken(doc, 'tbody', '', 'table');
        let after = null;
        if (section.tagName === 'TABLE') {
            section.insertBefore(token, first);
        } else {
            //Abschnitt am Token teilen: Zeilen davor | Token | Zeilen danach
            after = section.cloneNode(false);
            while (last.nextSibling) {
                after.appendChild(last.nextSibling);
            }
            section.parentNode.insertBefore(token, section.nextSibling);
        }

        let node = first;
        while (node) {
            const next = node.nextSibling;
            if (node.nodeType === Node.ELEMENT_NODE && TABLE_SECTION.test(node.tagName)) {
                //vom Parser erzeugtes <tbody> (Zeilen ohne eigenes tbody) — tbody darf nicht in tbody
                while (node.firstChild) {
                    token.appendChild(node.firstChild);
                }
                node.remove();
            } else {
                token.appendChild(node);
            }
            if (node === last) {
                break;
            }
            node = next;
        }

        if (after && after.hasChildNodes()) {
            token.parentNode.insertBefore(after, token.nextSibling);
        }
        if (section.tagName !== 'TABLE' && !section.textContent.trim() && !section.querySelector('*')) {
            section.remove();
        }

        //Quellcode ohne evtl. schon verpackte innere Tokens speichern (Twig bleibt in Kommentarform)
        const clone = token.cloneNode(true);
        unwrapTokens(clone);
        token.setAttribute('data-twig', encodeTwig(clone.innerHTML));
    }
};

//Container, in denen Block-Inhalte (z.B. Tabellen aus includes) gültiges HTML sind
const SAFE_CONTAINERS = /^(BODY|DIV|TD|TH|LI|BLOCKQUOTE|CENTER|SECTION|ARTICLE)$/;

//Twig, das Block-HTML erzeugen kann (includes rendern z.B. ganze Tabellen)
const producesBlockContent = source => /include\s*\(/.test(source) || /<(p|div|table|tr|ul|ol|li|h[1-6])\b/i.test(source);

//Tokens mit Block-Inhalt aus p/span/a-Kontexten herausheben: der HTML-Parser würde eine
//gerenderte Tabelle sonst aus dem Token hinausschieben (invalides Nesting) — sie wäre
//editierbar und käme bei jedem Render-Zyklus erneut dazu. Dazu werden die Vorfahren am
//Token gesplittet, bis das Token in einem tabellen-sicheren Container liegt.
const hoistBlockTokens = doc => {
    Array.from(doc.body.querySelectorAll(TOKEN_SELECTOR)).forEach(token => {
        if (token.parentElement && token.parentElement.closest(TOKEN_SELECTOR)) {
            //verschachtelt in einem äußeren Token — das wird als Ganzes behandelt
            return;
        }
        if (token.tagName === 'TBODY') {
            //Zeilen-Token sitzt bereits richtig in seiner Tabelle
            return;
        }
        const source = decodeTwig(token.getAttribute('data-twig'));
        if (!producesBlockContent(source)) {
            return;
        }

        let guard = 0;
        while (token.parentElement && !SAFE_CONTAINERS.test(token.parentElement.tagName) && guard++ < 30) {
            const parent = token.parentElement;
            const after = parent.cloneNode(false);
            while (token.nextSibling) {
                after.appendChild(token.nextSibling);
            }
            parent.parentNode.insertBefore(token, parent.nextSibling);
            if (after.hasChildNodes()) {
                parent.parentNode.insertBefore(after, token.nextSibling);
            }
            if (!parent.hasChildNodes()) {
                parent.remove();
            }
        }

        //Block-Inhalt braucht ein div — ein span würde vom Parser zerlegt
        if (token.tagName === 'SPAN') {
            const div = doc.createElement('div');
            Array.from(token.attributes).forEach(attr => div.setAttribute(attr.name, attr.value));
            while (token.firstChild) {
                div.appendChild(token.firstChild);
            }
            token.parentNode.replaceChild(div, token);
        }
    });
};

//HTML-Serialisierung escapt >, <, & — innerhalb von Twig-Delimitern muss der Code aber roh
//bleiben, sonst scheitert das Server-Rendering (z.B. {% if a &gt; b %})
const decodeEntitiesInTwig = html => html.replace(/({%[\s\S]*?%}|{{[\s\S]*?}})/g, twig => twig
    .replace(/&nbsp;/g, ' ')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&'));

//Gegenstück vor dem Parsen: "<" in Twig-Code (z.B. {{ dates|join('<br>') }}) würde der HTML-Parser
//als Tag lesen und den Ausdruck zerschneiden — er wird dann kein Token, sondern landet gerendert
//als fester Text in der Vorlage (gleiches Datum für alle Empfänger). "&" maskieren, damit
//z.B. "&nbsp;" im Twig-Code wörtlich bleibt statt zum Leerzeichen zu werden.
const escapeTwigForParser = html => html.replace(/({%[\s\S]*?%}|{{[\s\S]*?}})/g, twig => twig
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;'));

//Template (roher Twig-Code) → HTML mit Token-Wrappern für /api/email_preview
export const wrapTemplate = html => {
    const doc = parseHtml(escapeTwigForParser(protectTableTwig(html)));
    wrapTextNodeTokens(doc, ['expr', 'block']);
    wrapBlockTokens(doc);
    wrapTextNodeTokens(doc, ['statement']);
    wrapTableTokens(doc);
    hoistBlockTokens(doc);
    return decodeEntitiesInTwig(restoreTableTwig(doc.body.innerHTML));
};

//(bearbeitetes) Editor-HTML → rohes Template mit Twig-Code zurück
export const extractTemplate = html => {
    const doc = parseHtml(html);
    unwrapTokens(doc.body);
    return decodeEntitiesInTwig(restoreTableTwig(doc.body.innerHTML));
};

//Token "einfrieren": das Element wird durch seinen aktuell GERENDERTEN Inhalt ersetzt —
//der wird damit editierbar und gilt (als statischer Text) für alle Emails; der Twig-Code
//entfällt. Verschachtelte Tokens im Inhalt bleiben Tokens (rendern weiter pro Empfänger).
export const freezeToken = (html, tokenId) => {
    const doc = parseHtml(html);
    const token = doc.querySelector('[data-twig-id="' + tokenId + '"]');
    if (!token) {
        return html;
    }

    while (token.firstChild) {
        token.parentNode.insertBefore(token.firstChild, token);
    }
    token.remove();

    return doc.body.innerHTML;
};


/*
 * Betreff: der Input zeigt den GERENDERTEN Betreff, Bearbeitungen sollen aber als
 * Template für alle Empfänger gelten. Dazu werden die Twig-Ausdrücke vor dem Rendern
 * mit unsichtbaren Markern umschlossen; aus der Antwort lassen sich die gerenderten
 * Werte je Ausdruck herauslesen. Beim Bearbeiten werden die Werte im editierten Text
 * wieder lokalisiert — der Text drumherum wird zu den neuen statischen Teilen des
 * Templates, die Twig-Ausdrücke bleiben erhalten. Wird ein gerenderter Wert selbst
 * verändert (nicht mehr auffindbar), fällt der Betreff auf den literalen Text zurück.
 */

const SUBJECT_MARKER_START = '⁣'; //INVISIBLE SEPARATOR
const SUBJECT_MARKER_END = '⁤';   //INVISIBLE PLUS

//komplette (auch verschachtelte) if/for-Blöcke als EINE Einheit — sonst passt die Anzahl
//der gerenderten Werte nicht zur Anzahl der Ausdrücke (ein Block = ein Wert)
const isTwigUnit = segment => segment.type === 'expr' || segment.type === 'block' || segment.type === 'statement';

//Twig-Einheiten des Betreff-Templates in Dokument-Reihenfolge
export const subjectTwigParts = template => tokenizeTwig(template || '')
    .filter(isTwigUnit)
    .map(segment => segment.text);

//Template → markierte Fassung für /api/email_preview
export const markSubjectTemplate = template => tokenizeTwig(template || '')
    .map(segment => (isTwigUnit(segment) ? SUBJECT_MARKER_START + segment.text + SUBJECT_MARKER_END : segment.text))
    .join('');

//gerenderte, markierte Antwort → { display, values, statics } (null bei kaputten Markern);
//statics[i] steht vor values[i], statics[values.length] ist der Rest dahinter
export const parseMarkedSubject = rendered => {
    const chunks = (rendered || '').split(SUBJECT_MARKER_START);
    const values = [];
    const statics = [chunks[0]];
    let display = chunks[0];

    for (let i = 1; i < chunks.length; i++) {
        const endIndex = chunks[i].indexOf(SUBJECT_MARKER_END);
        if (endIndex === -1) {
            return null;
        }
        values.push(chunks[i].slice(0, endIndex));
        statics.push(chunks[i].slice(endIndex + SUBJECT_MARKER_END.length));
        display += chunks[i].slice(0, endIndex) + chunks[i].slice(endIndex + SUBJECT_MARKER_END.length);
    }

    return { display, values, statics };
};

